/**
 * 日历工具 —— **账本本地日历日的唯一实现**
 *
 * 依据：规格 §22.1 / §22.4 · 架构 **§4.5**（日期与时间存储）· **§8.1**（水位补跑 / 月末 clamp）
 * · `DR-023` D3。
 *
 * ## 🔴 两条存储铁律（架构 §4.5）
 * 1. **交易日 / 周期规则日期 / 预算窗口边界 = 账本本地日历日 `YYYY-MM-DD`（无时区）**
 *    —— 交易日是**日历事实**，不是时刻。用 UTC 毫秒表示它，任何一处忘记换算就漂移一天。
 * 2. **时间戳（`createdAt` / `updatedAt` / `pushedAt` / `grantedAt`）= UTC 毫秒**。
 *
 * ## 为什么要有这个文件
 * 本模块此前散落在 `budget.ts` 的私有函数里。`server` 的**统一分页默认时间窗**
 * （架构 §6.4「近 3 个月」）与**调度水位补跑**（架构 §8.1）同样需要它 ——
 * 若各写一份，就是铁律 24 点名的「同一机制两处各答一遍」。
 * ⇒ **收上来成为唯一实现**，`budget.ts` 改为消费它（行为不变，由原有测试兜底）。
 *
 * ## 表示法约定
 * - 「日」一律是 `YYYY-MM-DD` **字符串**（可直接字符串比较大小 = 日期比较）。
 * - 字符串已是"本地日"⇒ 做天数运算时**按 UTC 零点解析**，**不再套本地时区**
 *   （再套一次就是双重偏移）。
 */

import { divideExact } from './money.js';

/** 一天的毫秒数 */
export const MS_PER_DAY = 86400000;

/** 各月所属季度的**首个月份**（查表，避免 `Math.floor(mo / 3)` 这类除法 —— 守 §22.1） */
export const QUARTER_FIRST_MONTH = [1, 1, 1, 4, 4, 4, 7, 7, 7, 10, 10, 10] as const;

/** 解析后的日历日 */
export interface Ymd {
  y: number;
  m: number;
  d: number;
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` 形态校验（**不校验日期是否真实存在**，只校验形态） */
export function isDayString(value: unknown): value is string {
  return typeof value === 'string' && DAY_PATTERN.test(value);
}

/**
 * `YYYY-MM-DD` **形态 + 真实性**双校验（`2026-13-01` / `2026-02-30` ⇒ `false`）。
 *
 * 🔴 为什么不能只用 {@link isDayString}：它自己写着「不校验日期是否真实存在」。
 * 形态对但值非法的日期会一路走到 `addMonths()` / `Date.UTC()` —— 那里
 * `Date.UTC(2026, 12, 1)` 会**静默归一**成 2027-01-01，于是"截止日 2026-13-01"
 * 变成一个看起来完全正常的日期（铁律 99）。
 *
 * 用途：① 写库前判"能不能存"（`goal.ts` 的截止日）；② 读库后判"能不能算"
 * （投资域 `maturityTextOf`：到期日脏 ⇒ 如实显示"未填"，不拿归一后的日期算剩余天数）。
 */
export function isRealDayString(day: string): boolean {
  if (!isDayString(day)) return false;
  const { y, m, d } = parseDay(day);
  return fromYmd(y, m, d) === day;
}

/** 解析 `YYYY-MM-DD`；形态不符即抛（**不静默兜底**） */
export function parseDay(day: string): Ymd {
  const m = DAY_PATTERN.exec(day);
  if (m === null) {
    throw new RangeError(`日期须为 YYYY-MM-DD（账本本地日历日）：${day}`);
  }
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** 由 `Date`（按 **UTC 字段**读取）取 `YYYY-MM-DD` */
export function toDayString(dt: Date): string {
  const y = dt.getUTCFullYear();
  const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/** 由年月日构造 `YYYY-MM-DD`（`Date.UTC` 会自动归一化溢出值，故 `m = 13` 也安全） */
export function fromYmd(y: number, m: number, d: number): string {
  return toDayString(new Date(Date.UTC(y, m - 1, d)));
}

/** 「日」→ UTC 零点的毫秒（**仅用于运算**；不要把结果当"时刻"存库） */
export function dayToUtcMs(day: string): number {
  const { y, m, d } = parseDay(day);
  return Date.UTC(y, m - 1, d);
}

/** UTC 毫秒 → 「日」（按 **UTC** 读取；仅在已知输入就是 UTC 零点时使用） */
export function utcMsToDay(ms: number): string {
  return toDayString(new Date(ms));
}

/** 加减天数（负数即减）。溢出跨月 / 跨年由 `Date.UTC` 归一化 */
export function addDays(day: string, n: number): string {
  const { y, m, d } = parseDay(day);
  return toDayString(new Date(Date.UTC(y, m - 1, d + n)));
}

/**
 * `to − from` 的天数（**可负**）。
 * 🔴 两端均为 UTC 零点 ⇒ 差值必为整日倍数，故用 `divideExact`（不引入浮点）。
 */
export function daysBetween(from: string, to: string): number {
  return divideExact(dayToUtcMs(to) - dayToUtcMs(from), MS_PER_DAY);
}

/**
 * 星期几：**`0` = 周日**，`1`–`6` = 周一至周六（`Date.getUTCDay()` 的约定，不另立一套）。
 *
 * 🔴 为什么必须走 `dayToUtcMs`（而不是 `new Date('2026-10-18')`）：
 * 后者对 `YYYY-MM-DD` 解析为 UTC 零点，**在部分环境（含老 Node）会按本地时区**，
 * 中国时区（UTC+8）下得到的是「前一天 16:00」⇒ 星期几整体错一天。
 * 本仓「本地日历日」是**字符串**，只有 `dayToUtcMs` 是它的唯一正规化入口。
 *
 * 用途：H 域周检视（`AC-H1.7`：**周日 09:00**）判「今天是不是周日」。
 */
export function weekdayOf(day: string): number {
  return new Date(dayToUtcMs(day)).getUTCDay();
}

/** 数值夹取到 `[lo, hi]` */
export function clampInt(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/** 该年该月的天数（`Date.UTC(y, m, 0)` = 下月第 0 天 = 本月最后一天） */
export function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** 当月首日 */
export function monthStart(day: string): string {
  const { y, m } = parseDay(day);
  return fromYmd(y, m, 1);
}

/** **次月**首日 —— 即「本月」半开区间 `[monthStart, monthEndExclusive)` 的右端 */
export function monthEndExclusive(day: string): string {
  const { y, m } = parseDay(day);
  return fromYmd(y, m + 1, 1);
}

/**
 * 加减月份，**带月末 clamp**（架构 §8.1：31 日规则遇 2 月取该月最后一天）。
 *
 * 例：`2026-01-31 +1 月 → 2026-02-28`；`2026-03-31 +1 月 → 2026-04-30`。
 */
export function addMonths(day: string, n: number): string {
  const { y, m, d } = parseDay(day);
  // 用 Date.UTC 的归一化能力求"目标年月"，避免 `总月数 / 12` 这类除法（守 §22.1）
  const probe = new Date(Date.UTC(y, m - 1 + n, 1));
  const ny = probe.getUTCFullYear();
  const nm = probe.getUTCMonth() + 1;
  return fromYmd(ny, nm, clampInt(d, 1, lastDayOfMonth(ny, nm)));
}

/** 季度首日 */
export function quarterStart(day: string): string {
  const { y, m } = parseDay(day);
  return fromYmd(y, QUARTER_FIRST_MONTH[m - 1] ?? 1, 1);
}

/** 年初日 */
export function yearStart(day: string): string {
  return fromYmd(parseDay(day).y, 1, 1);
}

/**
 * 「**近 N 个自然月**（含本月）」窗口的起点 —— 架构 §6.4 的**默认时间窗**（`DR-006`：3 个月）。
 *
 * ⚠️ **口径**：`today = 2026-09-29`、`months = 3` ⇒ `2026-07-01`（= 7 月、8 月、9 月三个月）。
 * 右端**不含今天之后**，由调用方以 `today` 或当前时刻收口。
 */
export function recentMonthsStart(today: string, months: number): string {
  if (months < 1) throw new RangeError(`窗口月数须 ≥ 1：${months}`);
  return monthStart(addMonths(today, -(months - 1)));
}

// ─────────────────────────── 时区（架构 §4.5 / §8.1） ───────────────────────────

/** 格式化器缓存（`Intl.DateTimeFormat` 构造代价高，且时区集合是有限小集） */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

/** 时区是否被运行环境识别（🔴 用于启动期 fail-fast，**不静默回退到 UTC**） */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone });
    return true;
  } catch {
    return false;
  }
}

function dayFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

/** 时刻在指定时区下的**本地日历日 + 时分** */
export interface LocalParts extends Ymd {
  hour: number;
  minute: number;
}

/**
 * UTC 毫秒 → **账本时区**下的本地日历日与时分。
 *
 * 🔴 这是「同一个 `now`，在不同账本时区下是不同的一天」的唯一换算法
 * —— 各处禁止自己 `new Date().getDate()`（那会**按服务器时区**取，与账本时区无关）。
 */
export function localPartsOf(instantMs: number, timeZone: string): LocalParts {
  const parts = dayFormatter(timeZone).formatToParts(new Date(instantMs));
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type);
    return p === undefined ? NaN : Number(p.value);
  };
  // `hour12: false` 下部分环境下 24 时制的午夜会给出 24 ⇒ 归一到 0
  const hour = get('hour');
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    hour: hour === 24 ? 0 : hour,
    minute: get('minute'),
  };
}

/** UTC 毫秒 → **账本时区**下的本地日历日 `YYYY-MM-DD` */
export function localDayOf(instantMs: number, timeZone: string): string {
  const p = localPartsOf(instantMs, timeZone);
  return fromYmd(p.y, p.m, p.d);
}

/** 本地时钟的 `HH:mm`（如 `08:30`）—— 用于日志与展示，**不用于运算** */
export function localClockOf(instantMs: number, timeZone: string): string {
  const p = localPartsOf(instantMs, timeZone);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/**
 * **账本时区下的「某日某时刻」→ UTC 毫秒**（`localDayOf` / `localClockOf` 的**逆运算**）。
 *
 * 用途：把「每日 **08:30** 生成提醒」这类**本地时刻**换算成可排序 / 可当水位的 UTC 时刻
 * （架构 §4.3：**提醒触发窗口按账本时区计算后存 UTC 时刻**）。
 *
 * 🔴 **不能写成 `dayToUtcMs(day) + 8h`** —— 那等于把 `UTC+8` **硬编码**，
 * 而架构 §4.3 明定时区是 `ledgers.timezone` **字段**（P0 恰好是 `Asia/Shanghai`，不是恒定值）。
 *
 * 实现：先用 UTC 造一个初值，再按该时刻在目标时区的**实际**本地读数校正，**迭代两次收敛**
 * （第一次校正时区偏移，第二次兜住夏令时切换日的边界）。
 */
export function zonedClockToUtcMs(day: string, clock: string, timeZone: string): number {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const cm = /^(\d{2}):(\d{2})$/.exec(clock);
  if (dm === null) throw new RangeError(`日期格式须为 YYYY-MM-DD：${day}`);
  if (cm === null) throw new RangeError(`时刻格式须为 HH:mm：${clock}`);
  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const d = Number(dm[3]);
  const hh = Number(cm[1]);
  const mm = Number(cm[2]);
  if (hh > 23 || mm > 59 || mo < 1 || mo > 12) {
    throw new RangeError(`日/时刻越界：${day} ${clock}`);
  }

  const target = Date.UTC(y, mo - 1, d, hh, mm, 0, 0);
  let ms = target;
  for (let i = 0; i < 2; i += 1) {
    const p = localPartsOf(ms, timeZone);
    const actual = Date.UTC(p.y, p.m - 1, p.d, p.hour, p.minute, 0, 0);
    if (actual === target) break;
    ms += target - actual;
  }
  return ms;
}

/** 两个「日」之间的**全部日历日**（含起、**不含**止）；`from >= to` 时返回空数组 */
export function eachDayExclusive(from: string, to: string): string[] {
  const out: string[] = [];
  const total = daysBetween(from, to);
  for (let i = 0; i < total; i += 1) out.push(addDays(from, i));
  return out;
}

/**
 * 半开区间 `[start, end)` 内的全部「日」（含起、不含止）。
 * 🔴 **一律半开区间** —— 与 `budget.ts` 的窗口口径同源（G 域 §4.2：杜绝边界双计）。
 */
export function eachDayInWindow(start: string, endExclusive: string): string[] {
  return eachDayExclusive(start, endExclusive);
}
